// Fiche d'une intervention : signalement d'origine, chronologie du workflow, missions (affectation,
// réaffectation avec version serveur, annulation), rapports de terrain avec photos avant et après,
// transitions (planifier, commencer, soumettre, refuser, valider, clôturer, annuler) selon les
// droits et l'état ; un conflit de synchronisation bloque la validation jusqu'à sa décision.
import { Bandeau, Bouton, Champ, DateChamp, DialogueConfirmation, Lien, LienBouton, Panneau, PuceEtat, Selection, ZoneTexte } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { annulerMission, creerMission, INVALIDER_INTERVENTIONS, modifierMission, transiterIntervention, useEquipes, useIntervention } from './api';
import { aujourdhuiIso, duree, LIBELLES_TRANSITION, libelleEtatIntervention, libelleEtatMission, libelleTypeRapport, montant, TON_ETAT_MISSION } from './logique';
import { puceEtat, pucePriorite } from './PageInterventions';
import { Photos } from './Photo';
import type { InterventionDetail, Mission, Rapport, TransitionPossible } from './types';
import commun from '../../commun/commun.module.css';
import styles from './interventions.module.css';

function Transition({ i, t }: { i: InterventionDetail; t: TransitionPossible }) {
  const def = LIBELLES_TRANSITION[t.code];
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [valeur, setValeur] = useState(def?.champ === 'planifiee_le' ? (i.planifiee_le ?? aujourdhuiIso()) : '');
  const action = useAction({
    executer: () => transiterIntervention(i.id, t.code, { motif: motif.trim() || undefined, ...(def?.champ === 'planifiee_le' && valeur ? { planifiee_le: valeur } : {}), ...(def?.champ === 'cout_reel' && valeur ? { cout_reel: Number(valeur) } : {}) }),
    succes: (r) => `Intervention ${r.numero} : ${libelleEtatIntervention(r.etat).toLowerCase()}.`,
    invalider: INVALIDER_INTERVENTIONS,
    apres: () => setOuvert(false),
  });
  if (!def) return null;
  return (
    <>
      <Bouton variante={def.variante} onClick={() => setOuvert(true)}>
        {def.bouton}
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre={`${t.libelle} : ${i.numero}`}
        message={<p>L&apos;intervention passe à l&apos;état « {libelleEtatIntervention(t.vers)} »{t.code === 'cloturer' ? ' ; les demandeurs sont notifiés.' : t.code === 'planifier' ? ' ; les demandeurs sont notifiés de la date.' : '.'}</p>}
        libelleConfirmer={def.bouton}
        variante={def.variante === 'danger' ? 'danger' : undefined}
        enCours={action.enCours}
        confirmationDesactivee={(def.motif || t.motif_obligatoire) && motif.trim().length < 3}
        onConfirmer={() => action.lancer(undefined)}
        onAnnuler={() => {
          setOuvert(false);
          action.reinitialiser();
        }}
      >
        {def.champ === 'planifiee_le' ? <DateChamp libelle="Date planifiée" value={valeur} onChange={(e) => setValeur(e.target.value)} /> : null}
        {def.champ === 'cout_reel' ? <Champ libelle="Coût réel (F)" type="number" min={0} value={valeur} onChange={(e) => setValeur(e.target.value)} facultatif /> : null}
        <ZoneTexte libelle={def.motif || t.motif_obligatoire ? 'Motif (trois caractères au moins)' : 'Commentaire'} value={motif} onChange={(e) => setMotif(e.target.value)} required={def.motif || t.motif_obligatoire} facultatif={!(def.motif || t.motif_obligatoire)} rows={3} />
        {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

function FormulaireMission({ i, mission, onFermer }: { i: InterventionDetail; mission: Mission | null; onFermer: () => void }) {
  const equipes = useEquipes();
  const [v, setV] = useState({ equipe_id: mission?.equipe.id ?? i.equipe?.id ?? '', agent: mission?.agent?.appartenance_id ?? '', date: mission?.date ?? i.planifiee_le ?? aujourdhuiIso(), consignes: mission?.consignes ?? '' });
  const equipe = (equipes.data ?? []).find((e) => e.id === v.equipe_id);
  const action = useAction({
    executer: () =>
      mission
        ? modifierMission(mission.id, { equipe_id: v.equipe_id, agent_appartenance_id: v.agent || null, date: v.date, consignes: v.consignes || null })
        : creerMission(i.id, { equipe_id: v.equipe_id, agent_appartenance_id: v.agent || undefined, date: v.date, consignes: v.consignes || undefined }),
    succes: (m) => (mission ? `Mission modifiée : version ${m.version}, agent notifié.` : 'Mission affectée, agent notifié.'),
    invalider: INVALIDER_INTERVENTIONS,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message;
  return (
    <DialogueConfirmation
      ouvert
      titre={mission ? `Modifier la mission du ${date(mission.date)}` : `Affecter une mission : ${i.numero}`}
      message={<p>{mission ? 'La version serveur de la mission augmente : un rapport saisi sur l\'ancienne version sera reçu en conflit et attendra votre décision.' : 'La première mission planifie l\'intervention ; l\'agent désigné, ou le chef de l\'équipe, est notifié.'}</p>}
      libelleConfirmer={mission ? 'Enregistrer' : 'Affecter'}
      enCours={action.enCours}
      confirmationDesactivee={!v.equipe_id || !v.date}
      onConfirmer={() => action.lancer(undefined)}
      onAnnuler={onFermer}
    >
      <Selection libelle="Équipe" optionVide="Choisir" options={(equipes.data ?? []).filter((e) => e.actif).map((e) => ({ valeur: e.id, libelle: `${e.nom} (${e.type === 'prestataire' ? e.prestataire?.nom ?? 'prestataire' : 'municipale'})` }))} value={v.equipe_id} onChange={(e) => setV({ ...v, equipe_id: e.target.value, agent: '' })} erreur={champ('equipe_id')} required />
      <Selection libelle="Agent" optionVide="Toute l'équipe (chef notifié)" options={(equipe?.membres ?? []).map((m) => ({ valeur: m.appartenance_id, libelle: `${m.nom ?? m.appartenance_id.slice(0, 8)}${m.role === 'chef' ? ' (chef)' : ''}` }))} value={v.agent} onChange={(e) => setV({ ...v, agent: e.target.value })} erreur={champ('agent_appartenance_id')} facultatif />
      <DateChamp libelle="Date de la mission" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} erreur={champ('date')} required />
      <ZoneTexte libelle="Consignes" value={v.consignes} onChange={(e) => setV({ ...v, consignes: e.target.value })} facultatif rows={3} />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </DialogueConfirmation>
  );
}

function Annulation({ m, onFermer }: { m: Mission; onFermer: () => void }) {
  const [motif, setMotif] = useState('');
  const action = useAction({ executer: () => annulerMission(m.id, motif.trim()), succes: 'Mission annulée.', invalider: INVALIDER_INTERVENTIONS, apres: onFermer });
  return (
    <DialogueConfirmation ouvert titre={`Annuler la mission du ${date(m.date)}`} message={<p>La mission est annulée avec un motif ; sa version serveur augmente.</p>} libelleConfirmer="Annuler la mission" variante="danger" enCours={action.enCours} confirmationDesactivee={motif.trim().length < 3} onConfirmer={() => action.lancer(undefined)} onAnnuler={onFermer}>
      <ZoneTexte libelle="Motif" value={motif} onChange={(e) => setMotif(e.target.value)} required rows={3} />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </DialogueConfirmation>
  );
}

function CarteRapport({ r }: { r: Rapport }) {
  const c = r.contenu;
  return (
    <article className={styles.version} aria-label={`${libelleTypeRapport(r.type)} du ${dateHeure(r.recu_le)}`}>
      <h3>
        {libelleTypeRapport(r.type)} · <PuceEtat ton={r.etat === 'conflit' ? 'retard' : r.etat === 'ecarte' ? 'neutre' : 'ok'} libelle={r.etat === 'synchronise' ? 'Synchronisé' : r.etat === 'conflit' ? 'En conflit' : r.etat === 'retenu' ? 'Retenu' : 'Écarté'} />
      </h3>
      <dl className={commun.definitions}>
        <dt>Saisi sur l&apos;appareil le</dt>
        <dd>{dateHeure(r.enregistre_sur_appareil_le)}{r.auteur?.nom ? ` par ${r.auteur.nom}` : ''}</dd>
        <dt>Reçu le</dt>
        <dd>{dateHeure(r.recu_le)} (version attendue {r.version_attendue}, serveur {r.version_serveur})</dd>
        <dt>Constats</dt>
        <dd>{c.constats || 'non renseignés'}</dd>
        <dt>Travaux</dt>
        <dd>{c.travaux || 'non renseignés'}</dd>
        <dt>Matériaux</dt>
        <dd>{c.materiaux?.length ? <ul className={styles.materiaux}>{c.materiaux.map((m, k) => <li key={k}>{m.designation}{m.quantite !== undefined ? ` : ${m.quantite}${m.unite ? ` ${m.unite}` : ''}` : ''}</li>)}</ul> : 'aucun'}</dd>
        <dt>Durée</dt>
        <dd>{duree(c.duree_minutes)}{c.termine ? ' · mission terminée' : ''}</dd>
      </dl>
      <Photos photos={r.photos} vide="Aucune photo jointe au rapport." />
    </article>
  );
}

export function PageIntervention() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const fiche = useIntervention(id);
  const i = fiche.data;
  const [mission, setMission] = useState<{ ouvert: boolean; edition: Mission | null }>({ ouvert: false, edition: null });
  const [annulation, setAnnulation] = useState<Mission | null>(null);
  const peutModifier = peut(contexte, 'intervention', 'modifier');
  const ouverte = i ? !['cloturee', 'annulee'].includes(i.etat) : false;
  return (
    <>
      <EnTetePage
        etiquette={i ? `Intervention, ${i.categorie_libelle}` : 'Intervention'}
        titre={i ? `${i.numero} · ${i.titre}` : 'Intervention'}
        description={i ? <span className={commun.ligne}>{puceEtat(i)}{pucePriorite(i)}<span>{i.adresse ?? ''}</span></span> : null}
        actions={
          <>
            <LienBouton href="/interventions" icone={ArrowLeft}>
              Interventions
            </LienBouton>
            {i ? i.transitions_possibles.map((t) => <Transition key={`${t.code}-${t.vers}`} i={i} t={t} />) : null}
          </>
        }
      />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L3-A" fonction="La fiche de l'intervention">
        {i ? (
          <>
            {i.missions_en_conflit > 0 ? (
              <Bandeau type="attention">
                Un rapport reçu du terrain est en conflit avec la version serveur d&apos;une mission : la validation attend la décision du responsable technique (<Lien href="/interventions/conflits">Conflits</Lien>).
              </Bandeau>
            ) : null}
            <div className={commun.deuxColonnes}>
              <div className={commun.pile}>
                <Panneau titre="Intervention">
                  {i.description ? <p className={styles.description}>{i.description}</p> : null}
                  <dl className={commun.definitions}>
                    <dt>Origine</dt>
                    <dd>{i.signalement ? <Lien href={`/interventions/signalements/${i.signalement.id}`}>Signalement {i.signalement.numero ?? ''}</Lien> : 'Programme de la collectivité'}</dd>
                    <dt>Service</dt>
                    <dd>{i.service?.nom ?? 'non renseigné'}</dd>
                    <dt>Équipe</dt>
                    <dd>{i.equipe?.nom ?? 'à désigner'}</dd>
                    <dt>Planifiée le</dt>
                    <dd>{i.planifiee_le ? date(i.planifiee_le) : 'non planifiée'}</dd>
                    <dt>Début des travaux</dt>
                    <dd>{i.debut ? dateHeure(i.debut) : 'non commencés'}</dd>
                    <dt>Coûts</dt>
                    <dd>estimé {montant(i.cout_estime)}, réel {montant(i.cout_reel)}</dd>
                    {i.motif_annulation ? (
                      <>
                        <dt>Motif d&apos;annulation</dt>
                        <dd>{i.motif_annulation}</dd>
                      </>
                    ) : null}
                  </dl>
                  {i.signalement_detail ? <Photos photos={i.signalement_detail.photos} vide="Aucune photo du signalement." /> : null}
                </Panneau>
                <Panneau titre={`Missions (${i.missions_liste.length})`} actions={peutModifier && ouverte ? <Bouton variante="secondaire" petit onClick={() => setMission({ ouvert: true, edition: null })}>Affecter une mission</Bouton> : null}>
                  {i.missions_liste.length === 0 ? <p>Aucune mission : affecter une équipe pour planifier l&apos;intervention.</p> : null}
                  <ul className={styles.chronologie}>
                    {i.missions_liste.map((m) => (
                      <li key={m.id}>
                        <time dateTime={m.date}>{date(m.date)}</time>
                        <span>
                          <span className={styles.resume}>
                            <PuceEtat ton={TON_ETAT_MISSION[m.etat]} libelle={libelleEtatMission(m.etat)} />
                            <strong>{m.equipe.nom}</strong>
                            <span>{m.agent?.nom ? `agent ${m.agent.nom}` : 'toute l\'équipe'} · version {m.version} · {m.rapports} rapport{m.rapports > 1 ? 's' : ''}</span>
                          </span>
                          {m.consignes ? <span>Consignes : {m.consignes}</span> : null}
                          {m.motif_annulation ? <span>Annulée : {m.motif_annulation}</span> : null}
                          {peutModifier && !['validee', 'annulee'].includes(m.etat) ? (
                            <span className={styles.actionsFiche}>
                              <Bouton variante="secondaire" petit onClick={() => setMission({ ouvert: true, edition: m })}>
                                Réaffecter ou modifier
                              </Bouton>
                              <Bouton variante="danger" petit onClick={() => setAnnulation(m)}>
                                Annuler la mission
                              </Bouton>
                            </span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Panneau>
                <Panneau titre={`Rapports de terrain (${i.rapports_liste.length})`}>
                  {i.rapports_liste.length === 0 ? <p>Aucun rapport reçu. Les rapports sont saisis sur l&apos;application terrain et synchronisés au retour du réseau.</p> : null}
                  <div className={commun.pile}>
                    {i.rapports_liste.map((r) => (
                      <CarteRapport key={r.id} r={r} />
                    ))}
                  </div>
                </Panneau>
              </div>
              <div className={commun.pile}>
                <Panneau titre="Chronologie">
                  <ol className={styles.chronologie}>
                    {i.historique.map((e) => (
                      <li key={e.id}>
                        <time dateTime={e.horodatage}>{dateHeure(e.horodatage)}</time>
                        <span>
                          {e.type === 'demarrage' ? 'Création' : e.type === 'retour' ? 'Retour' : e.type === 'reouverture' ? 'Réouverture' : (e.transition_code ?? e.type)} → {libelleEtatIntervention(e.etat_apres)}
                          {e.auteur?.nom ? ` · ${e.auteur.nom}` : ''}
                          {e.motif ? ` · ${e.motif}` : ''}
                        </span>
                      </li>
                    ))}
                  </ol>
                </Panneau>
              </div>
            </div>
            {mission.ouvert ? <FormulaireMission i={i} mission={mission.edition} onFermer={() => setMission({ ouvert: false, edition: null })} /> : null}
            {annulation ? <Annulation m={annulation} onFermer={() => setAnnulation(null)} /> : null}
          </>
        ) : null}
      </EtatRequete>
    </>
  );
}
